import dayjs from "dayjs";
import { atom, DefaultValue, selector, selectorFamily } from "recoil";

import { currentProject, projectUpdate } from "lib/store/project";
import {
  getAppStoreStatistics,
  getChannelTalkStatistics,
  getGooglePlayStatistics,
  getUsageSummary,
  getWordCloud,
  getWordsAspects,
  getWordsFromAspect,
  getWordsList,
  getWordStatistics,
  aspectListType,
  threadResultsListType,
  threadResultsType,
  usageObjectType,
  wordCloudType,
} from "lib/store/dashboard";
import { dateState } from "lib/store/common";
import { aspectMaxPageState } from "lib/store/sentiment";

//사용량
export const asyncGetDashboardSummary = selector({
  key: "DASHBOARD/get_statics",
  get: async ({ get }) => {
    get(projectUpdate);
    const pid = get(currentProject);

    const { range } = get(dateState);
    if (isNaN(pid) || pid === 0) return;
    let returnVal: usageObjectType = {
      words: { count: 0, negative: 0, positive: 0, neutral: 0 },
      channel_talk: { count: 0, negative: 0, positive: 0, neutral: 0 },
      google_play: { count: 0, negative: 0, positive: 0, neutral: 0 },
    };
    try {
      returnVal = {
        ...(await getUsageSummary(pid, {
          start: range.first.format("YYYY-MM-DD"),
          end: range.last.format("YYYY-MM-DD"),
        })),
      };
    } catch (e) {
      console.log(e);
    }
    return returnVal;
  },
});

//wordCloud

export const asyncGetWordCloud = selector({
  key: "DASHBOARD/word-cloud",
  get: async ({ get }) => {
    get(projectUpdate);
    const pid = get(currentProject);

    const { range } = get(dateState);
    if (isNaN(pid) || pid === 0) return;
    let returnVal: wordCloudType[] = [{ word: "", count: 0 }];
    try {
      returnVal = [
        ...(await getWordCloud(pid, {
          n: 30,
          start: range.first.format("YYYY-MM-DD"),
          end: range.last.format("YYYY-MM-DD"),
        })),
      ];
    } catch (e) {
      console.log(e);
    }
    return returnVal;
  },
});

const graphFormatting = (arr: { date: string; count: number }[]) =>
  arr.reduce((data, { count }) => [...data, count], [] as number[]);
//그래프
export const asyncGetDashboardGraph = selectorFamily({
  key: "DASHBOARD/get_graph",
  get:
    ({ unit }: { unit: "day" | "month" | "week" }) =>
    async ({ get }) => {
      const pid = get(currentProject);
      const { range } = get(dateState);

      if (isNaN(pid) || pid === 0) return;

      let returns: {
        labels: string[];
        datasets: {
          data: number[];
          fill: boolean;
          backgroundColor: string;
          borderColor: string;
          id: string;
          label: string;
        }[];
      } = { labels: [], datasets: [] };

      try {
        const words = await getWordStatistics(pid, {
          unit,
          start: range.first.format("YYYY-MM-DD"),
          end: range.last.format("YYYY-MM-DD"),
        });

        const channelTalk = await getChannelTalkStatistics(pid, {
          unit,
          start: range.first.format("YYYY-MM-DD"),
          end: range.last.format("YYYY-MM-DD"),
        });

        const googlePlay = await getGooglePlayStatistics(pid, {
          unit,
          start: range.first.format("YYYY-MM-DD"),
          end: range.last.format("YYYY-MM-DD"),
        });
        const appStore = await getAppStoreStatistics(pid, {
          unit,
          start: range.first.format("YYYY-MM-DD"),
          end: range.last.format("YYYY-MM-DD"),
        });

        returns = {
          labels: words.map(({ date }: { date: string }) =>
            dayjs(date).format("MM.DD")
          ),
          datasets: [
            {
              data: graphFormatting(words),
              fill: false,
              backgroundColor: "#111",
              borderColor: "#111",
              id: "words",
              label: "words",
            },
            {
              data: graphFormatting(channelTalk),
              fill: false,
              backgroundColor: "#80ba27",
              borderColor: "#80ba27",
              id: "channel-talk",
              label: "channel talk",
            },
            {
              data: graphFormatting(googlePlay),
              fill: false,
              backgroundColor: "#0084ff",
              borderColor: "#0084ff",
              id: "google-play",
              label: "google play",
            },
            {
              data: graphFormatting(appStore),
              fill: false,
              backgroundColor: "#ee4731",
              borderColor: "#ee4731",
              id: "app-store",
              label: "app store",
            },
          ],
        };
      } catch (e) {
        console.log(e);
      }
      return returns;
    },
});

export const threadListState = atom({
  key: "THREAD/list_state",
  default: {
    count: 0,
    results: [],
    total_pages: 0,
  } as threadResultsListType,
});

// thread 리스트를 가져옴
export const asyncGetThreadList = selectorFamily<threadResultsListType, number>(
  {
    key: "DASHBOARD/get_thread_list",
    get:
      (page: number) =>
      async ({ get }) => {
        get(projectUpdate);
        const pid = get(currentProject);
        const { range } = get(dateState);
        const type: any = get(currentThreadFilter);
        let returnVal: threadResultsListType = {
          count: 0,
          results: [],
          total_pages: 0,
        };

        if (page === 0) return returnVal;

        if (isNaN(pid) || pid === 0) return returnVal;

        const data = {
          page,
          size: get(threadSize),
          start: range.first.format("YYYY-MM-DD"),
          end: range.last.format("YYYY-MM-DD"),
          type: Object.keys(type).reduce(
            (a, c) => (!!type[c] ? `${a}${c},` : a),
            ""
          ),
        };
        try {
          returnVal = [{ ...(await getWordsList(pid, data)) }].reduce(
            (accm, { count, total_pages, results }) => {
              return {
                count: accm.count + count,
                total_pages: accm.total_pages + total_pages,
                results: [...accm.results, ...results].map((i, idx) => ({
                  ...i,
                  idx: accm.count + count - ((data.page - 1) * data.size + idx),
                })),
              };
            },
            returnVal
          );
        } catch (e) {
          console.log(e);
        }

        return returnVal;
      },

    set:
      () =>
      ({ get, set }, newValue) => {
        console.log(newValue);
        if (!(newValue instanceof DefaultValue)) {
          set(threadListState, (cur) => {
            return {
              ...newValue,
              results: Array.from(
                new Set(
                  newValue.results
                    ? [...cur.results, ...newValue.results]
                    : cur.results
                )
              ),
            };
          });
        }
      },
  }
);

export const aspectListState = atom({
  key: "ASPECT/list_state",
  default: {
    count: 0,
    results: [],
    total_pages: 0,
  } as threadResultsListType,
});

export const asyncGetAspectList = selector({
  key: "DASHBOARD/get_aspect",
  get: async ({ get }) => {
    get(projectUpdate);
    const pid = get(currentProject);
    const { range } = get(dateState);
    const type: any = get(currentThreadFilter);
    if (isNaN(pid) || pid === 0) return;

    let returnVal: aspectListType = [];

    try {
      const aspect = await getWordsAspects(pid, {
        start: range.first.format("YYYY-MM-DD"),
        end: range.last.format("YYYY-MM-DD"),
        type: Object.keys(type).reduce(
          (a, c) => (!!type[c] ? `${a}${c},` : a),
          ""
        ),
      });
      returnVal = Object.keys(aspect).map((text) => ({
        aspect: text,
        positive: aspect[text].positive,
        negative: aspect[text].negative,
        neutral: aspect[text].neutral,
      }));
    } catch (e) {
      console.log(e);
    }

    return returnVal;
  },
});

export const asyncSearchAspect = selectorFamily<
  threadResultsListType,
  { page: number; aspect: string }
>({
  key: "DASHBOARD/search_aspect",
  get:
    ({ page, aspect }: { page: number; aspect: string }) =>
    async ({ get }) => {
      get(projectUpdate);

      const pid = get(currentProject);
      const size = get(threadSize);
      const { range } = get(dateState);
      const type: any = get(currentThreadFilter);

      let returnVal: threadResultsListType = {
        count: 0,
        total_pages: 0,
        next: null,
        previous: null,
        results: [],
      };

      if (!aspect || isNaN(pid) || pid === 0) return returnVal;

      try {
        const temp: threadResultsListType = {
          ...(await getWordsFromAspect(pid, {
            aspect,
            page,
            size,
            start: range.first.format("YYYY-MM-DD"),
            end: range.last.format("YYYY-MM-DD"),
            type: Object.keys(type).reduce(
              (a, c) => (!!type[c] ? `${a}${c},` : a),
              ""
            ),
          })),
        };

        returnVal = {
          ...temp,
          results: temp.results.map((i, idx) => ({
            ...i,
            idx: temp.count - ((page - 1) * size + idx),
          })),
        };
      } catch (e) {
        console.log(e);
      }
      return returnVal;
    },
  set:
    () =>
    ({ get, set }, newValue) => {
      if (!(newValue instanceof DefaultValue)) {
        set(aspectListState, (cur) => {
          //aspect 비교를 통해 리스트를 초기화 함
          const currentAspect = cur.results[0]?.result.aspect;
          const previousAspect = get(selectedAspect);

          if (currentAspect === previousAspect) {
            set(aspectMaxPageState, newValue.total_pages ?? 1);
            return {
              ...cur,
              results: Array.from(
                new Set(
                  newValue.results
                    ? [...cur.results, ...newValue.results]
                    : cur.results
                )
              ),
            };
          } else {
            return newValue;
          }
        });
      }
    },
});

// 현재 선택하고 있는 텍스트 아이템(대시보드)
export const currentSelected = atom({
  key: "DASHBOARD/current_selected",
  default: {
    id: 0,
    job: 0,
    plain_text: "",
    created_at: "1970-01-01",
    result: {
      aspect: "",
      sentiment: "",
      aspect_score: 0.0,
      sentiment_score: 0.0,
    },
  } as threadResultsType,
});
// 현재 선택하고 있는 텍스트 아이템 (스레드페이지)
export const currentThreadSelected = atom({
  key: "DASHBOARD/current_thread_selected",
  default: {
    id: 0,
    job: 0,
    plain_text: "",
    created_at: "1970-01-01",
    result: {
      aspect: "",
      sentiment: "",
      aspect_score: 0.0,
      sentiment_score: 0.0,
    },
  } as threadResultsType,
});

// 현재 선택하고 있는 텍스트 아이템 (aspect 페이지)
export const currentAspectSelected = atom({
  key: "DASHBOARD/current_aspect_thread_selected",
  default: {
    id: 0,
    job: 0,
    plain_text: "",
    created_at: "1970-01-01",
    result: {
      aspect: "",
      sentiment: "",
      aspect_score: 0.0,
      sentiment_score: 0.0,
    },
  } as threadResultsType,
});

export const threadSize = atom({
  key: "DASHBOARD/thread_size",
  default: 30,
});

export const selectedAspect = atom({
  key: "DASHBOARD/selected_aspect",
  default: null as string | null,
});

//현재 선택하고 있는 스레드 필터링
export const currentThreadFilter = atom({
  key: "DASHBOARD/thread_filter",
  default: {
    words: true,
    "app-store": true,
    "google-play": true,
    "channel-talk": true,
  },
});
